import { type ParticipantInDb, type SigningRequestSigneeRole } from '@/api';

/**
 * A signing participant is a contractual party. Only natural persons sign: a
 * legal-entity party signs through one of its representatives, who is the one
 * the request is delivered to. Both pages plus the wizard need the same three
 * strings out of it, so the rule lives here once.
 */

/**
 * Display name — a person's own name; a legal entity as "Doruk Arslan
 * (Google)", the representative who signs and the party they sign for. That
 * is also the name the provider shows the signer.
 */
export function participantName(participant: ParticipantInDb): string {
  const party = participant.contractual_party;
  const partyName = party?.name.trim() ?? '';
  const signer = participant.representative?.name.trim() ?? '';
  if (party?.kind === 'legal_entity' && signer) {
    return partyName ? `${signer} (${partyName})` : signer;
  }
  return partyName || signer || 'Unnamed participant';
}

/** Signing email — the person who signs: the party itself, or its representative. */
export function participantEmail(participant: ParticipantInDb): string {
  const party = participant.contractual_party;
  if (party?.kind === 'natural_person') return party.email?.trim() ?? '';
  return participant.representative?.email?.trim() ?? '';
}

/** The organisation line under the name: a legal entity's form and city, else the city. */
export function participantOrganisation(participant: ParticipantInDb): string {
  const party = participant.contractual_party;
  if (!party) return '';
  const parts = party.kind === 'legal_entity' ? [party.legal_form, party.city] : [party.city];
  return parts
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' · ');
}

/** The participants in one role, in the order the backend returned them. */
export function participantsInRole(
  participants: ParticipantInDb[] | undefined,
  role: SigningRequestSigneeRole
): ParticipantInDb[] {
  return (participants ?? []).filter(participant => participant.role === role);
}
